import { useEffect, useState } from 'react'
import { api } from '../../api'
import { useCachedFetch, setCached } from '../../lib/swr'
import RechargeSheet from '../../components/wallet/RechargeSheet'
import CheckoutPayment from '../../components/CheckoutPayment'
import RinaContextCard from '../../components/RinaContextCard'
import VersementModeCard from '../../components/wallet/VersementModeCard'
import { useAuth } from '../../AuthContext'
import { useCasquette } from '../../contexts/casquette'
import { useActionsPortail } from '../../contexts/navigation'
import PageHub from '../../components/PageHub'
import { avecAlpha } from '../../lib/couleurs'

/**
 * Page Wallet (Compte → « Mon wallet ») — prototype v7.2.1, ADR-058.
 *
 * Le contenu s'adapte a la CASQUETTE ACTIVE (hubMode) : c'est le pivot de
 * l'ecran. Scope producteur = hubMode 'producer', sinon client.
 * Journal par nature, net par scope, etancheite absolue (ADR-058 §1-2) :
 * le solde de l'autre casquette n'est rappele qu'en lecture seule (« cloisonne »).
 *
 * Donnees 100 % reelles : GET /wallet/me (ledger ADR-050) + statut Stripe
 * producteur (KYC). Les actions de demo du prototype (recharge, signaler un
 * manquant, valider la repartition) ne sont PAS recopiees : en prod ces
 * ecritures viennent des vrais evenements (prompt v7.2.1 §2). Le bloc
 * sequestre client apparaitra avec la phase sequestre (ADR-058 §3).
 */

const ORANGE = 'rgb(var(--c-primary))'
const TEAL = 'rgb(var(--c-teal))'
const VERT = 'rgb(var(--c-green))'
const CREME = 'rgb(var(--c-creme))'

// Lentilles rappelees dans la barre d'etancheite (memes emojis que le Compte).
const LENS = {
  client: { e: '🧑🏾', l: 'client' },
  producteur: { e: '🌱', l: 'producteur' },
}

const fmt = (n) => (n < 0 ? '−' : '') + Math.abs(n).toFixed(2).replace('.', ',') + ' €'
// INSTANT (`WalletTransaction.createdAt`) : le rendu local est le bon. Ne pas
// passer aux formateurs UTC de `api.js`, reserves aux colonnes `@db.Date`.
const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' })
  } catch {
    return ''
  }
}

/**
 * Nature d'affichage d'une ecriture du ledger : libelle + piece comptable
 * (mapping prompt v7.2.1 §5 sur les types existants — `REGLEMENT_OUT` du proto
 * = REGLEMENT producteur positif, pas de nouveau type, ADR-058 §6).
 */
function natureOf(l) {
  const montant = Number(l.montant)
  switch (l.type) {
    case 'VENTE_NET':
      return { label: 'Vente répartie (mon net)', piece: 'Stripe Connect' }
    case 'COMMISSION_DUE':
      return { label: 'Commission Manrina', piece: 'COM-2026' }
    case 'REGLEMENT':
      if (l.scope === 'PRODUCTEUR') {
        return montant >= 0
          ? { label: 'Commission réglée', piece: 'COM-2026 payée' }
          : { label: 'Net versé (transfert Stripe)', piece: 'Stripe Connect' }
      }
      return { label: 'Commande payée (avoirs)', piece: 'Paniers' }
    case 'RECHARGE':
      return { label: 'Rechargement', piece: 'Dépôt carte' }
    case 'REMBOURSEMENT':
      return { label: 'Remboursement', piece: 'Ajustement commande' }
    case 'AVOIR':
      return { label: 'Avoir', piece: 'Note de crédit' }
    case 'AJUSTEMENT':
      return { label: 'Ajustement', piece: 'Manrina' }
    default:
      return { label: l.type, piece: l.sourceModule || '' }
  }
}

// Ligne de la decomposition producteur (deux natures jamais fondues + net).
function Row({ l, v, teal = false, bold = false }) {
  return (
    <div className="flex items-baseline justify-between py-1">
      <span className={`text-[12.5px] ${bold ? 'font-bold' : ''} text-ink${bold ? '' : '/70'}`}>{l}</span>
      <span
        className={`font-serif text-[15px] whitespace-nowrap ${bold ? 'font-medium' : ''}`}
        style={{ color: bold ? VERT : teal ? '#0d6b5f' : ORANGE }}
      >
        {fmt(v)}
      </span>
    </div>
  )
}

function WalletContenu({ setEnPaiement }) {
  // Autonome (S48) : casquette, sous-vue paiement et navigations viennent des
  // contextes ; garde d'authentification integree (avant : condition HubShell).
  const { isAuthenticated } = useAuth()
  const { hubMode, isProducer } = useCasquette()
  const { ouvrirMarcheProducteur, setHubTab } = useActionsPortail()
  const onPayCommission = hubMode === 'producer' ? () => ouvrirMarcheProducteur({ tab: 'historique' }) : undefined
  const onOpenPaiements = hubMode === 'producer' ? () => { setHubTab('paiement'); window.scrollTo(0, 0) } : undefined
  const scope = hubMode === 'producer' ? 'producteur' : 'client'
  const autre = scope === 'producteur' ? 'client' : 'producteur'
  const accent = scope === 'producteur' ? ORANGE : TEAL

  const [flash, setFlash] = useState(null)
  const toast = (msg) => {
    setFlash(msg)
    setTimeout(() => setFlash(null), 2600)
  }

  // Solde + ledger reels (cache SWR : affichage instantane, revalidation en fond).
  // setCached('wallet/me', ...) previent TOUS les abonnes (cette page, la
  // pastille du header, la carte Compte) — synchronisation v0.73.4.
  const { data: wallet, loading } = useCachedFetch('wallet/me', api.getWallet)
  const [rechargeOpen, setRechargeOpen] = useState(false)
  // Paiement de la recharge en PLEINE PAGE (pas dans la feuille : le formulaire
  // carte est long et serait coupe sur mobile — meme pattern que le checkout).
  const [paiement, setPaiement] = useState(null) // { clientSecret, publishableKey, montant }

  // Le RETOUR vit dans le header (DESIGN-GUIDE §11.8) : on ne dessine pas de chip
  // ici, on signale seulement a App qu'une sous-vue est ouverte et comment la
  // fermer. La charge utile du paiement (clientSecret...) reste locale.
  useEffect(() => {
    setEnPaiement?.(paiement ? { fermer: () => setPaiement(null) } : false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paiement])
  const relireLedger = () =>
    api.getWallet().then((d) => setCached('wallet/me', d)).catch(() => {})
  // Verification active apres paiement : le backend re-lit le paiement chez
  // Stripe et credite (idempotent). On RELANCE tant que le credit n'est pas
  // confirme, et on affiche le RESULTAT REEL — jamais un faux succes (retour
  // terrain v0.73.3 : l'erreur etait avalee, toast « effectue » a tort).
  const refreshApresRecharge = (montant, paymentIntentId) => {
    toast('Paiement validé — vérification du crédit…')
    let tentatives = 0
    const verifier = () => {
      api.verifyWalletRecharge(paymentIntentId)
        .then((r) => {
          if (r?.credite) {
            relireLedger()
            toast(`✓ Wallet crédité de ${fmt(montant)}`)
          } else if (tentatives < 3) {
            tentatives += 1
            setTimeout(verifier, 3000)
          } else {
            relireLedger()
            toast(`Paiement reçu (statut Stripe : ${r?.statut || '?'}) — le crédit suivra sa confirmation.`)
          }
        })
        .catch((e) => {
          console.error('Verification recharge wallet en echec :', e)
          if (tentatives < 3) {
            tentatives += 1
            setTimeout(verifier, 3000)
          } else {
            relireLedger()
            toast('Paiement validé, mais la vérification du crédit ne répond pas — préviens Manrina si le solde n’apparaît pas.')
          }
        })
    }
    verifier()
  }
  // KYC producteur = statut du compte Stripe Connect (retrait bloque tant
  // qu'il n'est pas complet — ADR-058 §4). payoutsEnabled = versements ouverts.
  const { data: stripeStatus } = useCachedFetch(
    'producer/stripe/status',
    api.producer.getStripeStatus,
    { enabled: scope === 'producteur' },
  )
  const kycOk = !!stripeStatus?.payoutsEnabled

  // Garde d'authentification (apres les hooks — regle des hooks React) :
  // avant S48 c'etait la condition `hubTab === 'wallet' && isAuthenticated`
  // de HubShell qui empechait le rendu.
  if (!isAuthenticated) return null

  const soldeScope = Number(scope === 'producteur' ? wallet?.solde?.producteur : wallet?.solde?.client) || 0
  const soldeAutre = Number(scope === 'producteur' ? wallet?.solde?.client : wallet?.solde?.producteur) || 0
  const ledger = wallet?.ledger || []
  const lignesScope = ledger.filter((l) => l.scope === (scope === 'producteur' ? 'PRODUCTEUR' : 'CLIENT'))

  // Decomposition producteur (ADR-058 §1) : les lignes Marche (commission) et
  // les lignes des autres modules (ventes reparties) restent deux natures
  // distinctes ; le net n'est qu'un agregat de lecture par-dessus.
  const prodLignes = ledger.filter((l) => l.scope === 'PRODUCTEUR')
  const commissionSolde = prodLignes
    .filter((l) => l.sourceModule === 'MARCHE')
    .reduce((s, l) => s + Number(l.montant), 0)
  const due = Math.max(0, -commissionSolde) // commission encore due (>0)
  const ventesAcquises = prodLignes
    .filter((l) => l.sourceModule !== 'MARCHE')
    .reduce((s, l) => s + Number(l.montant), 0)

  const actions =
    scope === 'producteur'
      ? [
          {
            l: 'Payer ma commission marché avec mon solde',
            fn: () => {
              if (due <= 0) return toast('Aucune commission en attente.')
              // Le reglement se fait PAR evenement : direction Marche → Historique.
              if (onPayCommission) return onPayCommission()
              toast('Va dans Marché → Historique pour régler ta commission.')
            },
          },
          {
            l: kycOk ? 'Retirer mes fonds vers mon compte' : 'Retrait bloqué — compléter mon inscription',
            danger: !kycOk,
            fn: () => {
              // KYC incomplet : direction l'inscription Stripe embarquee (la
              // page Paiements n'est plus au menu — accessible d'ici, retour Enzo 10 juil)
              if (!kycOk) return onOpenPaiements ? onOpenPaiements() : toast('Retrait indisponible : termine ton inscription Stripe (KYC) pour débloquer.')
              if (soldeScope <= 0) return toast('Aucun fonds à retirer.')
              toast('Le retrait arrive très bientôt — ton solde reste acquis.')
            },
          },
        ]
      : [{ l: '+ Recharger mon wallet (carte)', fn: () => setRechargeOpen(true) }] // ADR-058 §7

  // ── Vue paiement de la recharge (pleine page, remplace le contenu wallet) ──
  if (paiement) {
    return (
      <div className="animate-fade-up">
        <p className="text-[10.5px] font-bold uppercase mb-2.5 ml-0.5" style={{ letterSpacing: 1.5, color: TEAL }}>
          Recharger mon wallet — {fmt(paiement.montant)}
        </p>
        <CheckoutPayment
          clientSecret={paiement.clientSecret}
          publishableKey={paiement.publishableKey}
          amount={paiement.montant}
          onBack={() => setPaiement(null)}
          onSuccess={() => {
            const { montant: m, paymentIntentId } = paiement
            setPaiement(null)
            refreshApresRecharge(m, paymentIntentId)
          }}
        />
      </div>
    )
  }

  return (
    <div className="animate-fade-up">
      {/* v7.2.4 : titre de page dans la bulle RINA (le retour « ← Compte » vit
          dans la ligne du header — App.jsx backChip). Plus de titre in-page. */}
      <RinaContextCard
       
        title="Mon wallet"
        message={scope === 'producteur'
          ? 'Votre solde producteur — vos ventes Paniers réparties, votre commission marché. Il reste séparé de votre solde particulier.'
          : 'Vos avoirs et rechargements — utilisables sur vos commandes. Séparés de votre activité producteur.'}
      />

      {/* Carte solde du scope actif — glass fonce, montant creme, halo accent */}
      <div className="glass-dark rounded-[26px] relative overflow-hidden mb-2.5" style={{ padding: '20px 20px 18px' }}>
        <div
          className="absolute -top-10 -right-10 w-[150px] h-[150px] rounded-full pointer-events-none"
          style={{ background: `radial-gradient(circle, ${avecAlpha(accent, 0.33)}, transparent 70%)` }}
        />
        <p className="relative text-[10.5px] font-bold uppercase" style={{ letterSpacing: 2, color: 'rgba(247,240,234,0.65)' }}>
          Solde {scope === 'producteur' ? 'producteur' : 'particulier'}
        </p>
        <p className="relative font-serif mt-0.5" style={{ fontSize: 42, lineHeight: 1.05, color: CREME }}>
          {loading && !wallet ? '…' : fmt(soldeScope)}
        </p>
        <p className="relative text-[12px] mt-2 leading-snug" style={{ color: 'rgba(247,240,234,0.8)' }}>
          {scope === 'producteur'
            ? soldeScope >= 0
              ? 'Net en ta faveur (Manrina te doit, commission déduite).'
              : 'Net en faveur de Manrina (commission > ventes).'
            : 'Avoirs disponibles pour payer tes commandes.'}
        </p>
      </div>

      {/* Etancheite : l'autre casquette en LECTURE SEULE — jamais utilisable ici */}
      {(scope === 'producteur' || isProducer) && (
        <div className="glass-surface rounded-[14px] mb-4 flex items-center gap-2 text-[12px]" style={{ padding: '9px 13px' }}>
          <span className="text-ink/60">
            {LENS[autre].e} Côté {LENS[autre].l} :
          </span>
          <strong className="font-serif text-[16px] text-ink">{fmt(soldeAutre)}</strong>
          <span className="ml-auto text-[11px] font-bold" style={{ color: ORANGE }}>
            cloisonné ✕
          </span>
        </div>
      )}

      {/* Decomposition producteur : deux natures, jamais fondues (ADR-058 §1) */}
      {scope === 'producteur' && (
        <div className="glass-surface rounded-[20px] mb-4" style={{ padding: '14px 16px' }}>
          <p className="text-[10.5px] font-bold uppercase mb-2.5" style={{ letterSpacing: 1.5, color: TEAL }}>
            Décomposition (deux natures)
          </p>
          <Row l="Ventes Paniers réparties (acquis)" v={ventesAcquises} teal />
          <Row l="Commission marché à payer" v={-due} />
          <div className="h-px my-2" style={{ background: 'rgba(7,62,61,0.1)' }} />
          <Row l="Net affiché" v={soldeScope} bold />
          <p className="text-[11px] mt-2 leading-relaxed text-ink/55">
            Deux pièces distinctes : versement Paniers (Stripe Connect) et commission marché (COM-2026). Le wallet
            montre le net, la compta garde les deux factures.
          </p>
        </div>
      )}

      {/* Bandeau KYC : retrait bloque tant que l'inscription Stripe n'est pas complete */}
      {scope === 'producteur' && !kycOk && (
        <div
          className="rounded-2xl mb-4 flex gap-2.5 items-start"
          style={{ padding: '12px 14px', background: 'rgba(244,137,83,0.1)', border: `1px solid ${avecAlpha(ORANGE, 0.33)}` }}
        >
          <span className="text-lg flex-shrink-0">🔒</span>
          <div>
            <p className="text-[13px] font-bold text-ink">Retrait des fonds bloqué</p>
            <p className="text-[11.5px] leading-relaxed mt-0.5 text-ink/65">
              Ton solde s'accumule, mais le retrait nécessite ton inscription Stripe complète (documents KYC). Tu peux
              déjà l'utiliser pour payer ta commission marché.
            </p>
            {onOpenPaiements && (
              <button
                onClick={onOpenPaiements}
                className="mt-2 bg-transparent border-none text-[12px] font-extrabold"
                style={{ color: ORANGE }}
              >
                Compléter mon inscription ›
              </button>
            )}
          </div>
        </div>
      )}

      {/* Versement des ventes Paniers (sequestre ADR-058 §3) — gere ICI, dans
          le wallet (retour Enzo 10 juil : c'est la que se gere l'argent) */}
      {scope === 'producteur' && <VersementModeCard onToast={toast} />}

      {/* Actions du scope */}
      {actions.length > 0 && (
        <>
          <p className="text-[10.5px] font-bold uppercase text-ink/50 mb-2 ml-0.5" style={{ letterSpacing: 1.5 }}>
            Actions
          </p>
          <div className="flex flex-col gap-2 mb-5">
            {actions.map((a, i) => (
              <button
                key={i}
                onClick={a.fn}
                className="glass-surface rounded-[14px] text-left text-[13.5px] font-semibold"
                style={{
                  padding: '13px 16px',
                  color: a.danger ? 'rgba(7,62,61,0.45)' : VERT,
                  fontStyle: a.danger ? 'italic' : 'normal',
                }}
              >
                {a.l}
              </button>
            ))}
          </div>
        </>
      )}

      {/* Historique du scope — montant signe : teal positif / orange negatif */}
      <p className="text-[10.5px] font-bold uppercase text-ink/50 mb-2 ml-0.5" style={{ letterSpacing: 1.5 }}>
        Mouvements — {scope === 'producteur' ? 'producteur' : 'particulier'}
      </p>
      <div className="flex flex-col gap-[7px]">
        {lignesScope.map((l) => {
          const n = natureOf(l)
          const montant = Number(l.montant)
          return (
            <div key={l.id} className="glass-surface rounded-[14px] flex items-center gap-2.5" style={{ padding: '11px 14px' }}>
              <div className="flex-1 min-w-0">
                <p className="text-[13.5px] font-semibold text-ink">{n.label}</p>
                <p className="text-[11.5px] mt-0.5 text-ink/55 truncate">
                  {[l.motif, fmtDate(l.createdAt), n.piece].filter(Boolean).join(' · ')}
                </p>
              </div>
              <span className="font-serif text-[17px] whitespace-nowrap" style={{ color: montant >= 0 ? '#0d6b5f' : ORANGE }}>
                {montant >= 0 ? '+' : '−'}
                {Math.abs(montant).toFixed(2).replace('.', ',')} €
              </span>
            </div>
          )
        })}
        {!loading && lignesScope.length === 0 && (
          <div className="glass-surface rounded-[14px] p-4 text-center text-[13px] text-ink/55">
            Aucun mouvement sur cette casquette.
          </div>
        )}
      </div>

      {flash && (
        <div
          className="glass-dark fixed left-1/2 -translate-x-1/2 rounded-[14px] text-[13px] font-semibold text-center z-[60]"
          style={{ bottom: 90, padding: '11px 18px', maxWidth: 340, color: CREME }}
        >
          {flash}
        </div>
      )}

      <RechargeSheet
        open={rechargeOpen}
        onClose={() => setRechargeOpen(false)}
        onReady={(p) => { setRechargeOpen(false); setPaiement(p) }}
      />
    </div>
  )
}

export default function PageWallet() {
  // Sous-vue paiement LOCALE (S52) : le contenu signale son ouverture (et
  // comment la fermer), le retour du header suit — sinon « ← Compte »
  // (le Wallet est une sous-page du Compte — ADR-058).
  const [enPaiement, setEnPaiement] = useState(false)
  const retour = enPaiement
    ? { label: 'Wallet', onClick: () => enPaiement.fermer() }
    : 'account'
  return <PageHub retour={retour}><WalletContenu setEnPaiement={setEnPaiement} /></PageHub>
}
